import { queryOptions, useQuery } from '@tanstack/react-query';
import { Loading } from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { z } from 'zod';
import api from '~/api';
import PageStatus from '~/components/PageStatus';
import { usePathParams } from '~/hooks/usePathParams';
import { useStoreId } from '~/providers/StoreUserProvider';

export type RegistrationReview = CrewMerchantUi.Registration & {
  refetch: () => Promise<void>;
};

const RegistrationContext = createContext<RegistrationReview | null>(null);

/** A registration's query, shared with anything else showing the registration the page has loaded (e.g. Sidekick). */
export const registrationQueryOptions = (
  storeId: ReturnType<typeof useStoreId>,
  registrationId: number | undefined,
) =>
  queryOptions({
    queryKey: ['registration', registrationId, storeId],
    queryFn: () =>
      api.store(storeId).registrations.get({
        registrationId: `${registrationId}`,
      }),
  });

function useRegistrationQuery() {
  const { registrationId } = usePathParams(
    z.object({ registrationId: z.coerce.number().optional() }),
  );
  const storeId = useStoreId();

  return useQuery({
    ...registrationQueryOptions(storeId, registrationId),
    enabled: !!registrationId,
    throwOnError: false,
  });
}

export function useRegistration() {
  const context = useContext(RegistrationContext);
  if (!context) {
    throw new Error(
      'useRegistration must be used within a RegistrationReviewProvider',
    );
  }
  return context;
}

/** only intended to be within "RegistrationReview" */
export function RegistrationReviewProvider({ children }: PropsWithChildren) {
  const {
    data: registration,
    error,
    isError,
    isPending,
    refetch,
  } = useRegistrationQuery();

  const value = useMemo<RegistrationReview | null>(() => {
    if (!registration) return null;
    return {
      ...registration,
      refetch: async () => {
        await refetch();
      },
    };
  }, [registration, refetch]);

  // TODO differentiate client and server errors, and only propagate server/request errors
  if (isError) return <PageStatus.Error error={error} />;

  if (isPending)
    return (
      <>
        <Loading />
        <PageStatus.Loading />
      </>
    );

  return (
    <RegistrationContext.Provider value={value}>
      {children}
    </RegistrationContext.Provider>
  );
}
